import React, { useState } from 'react';
import styled, { css } from 'styled-components';
import {
  brandColours,
  fontSize,
  inputStyles,
  labelStyles,
  minBreakpointQuery,
  sectionMargins,
  standardColours,
} from '../styles';
import { Button, Container, RecaptchaText } from './ui';
import { slugify, submitRecaptcha } from '../utils';

const StyledCreditApplicationForm = styled.section`
  ${sectionMargins()}
`;

const StyledWrapper = styled.div`
  margin-top: 20px;
  padding: 30px 50px;
  background-color: ${standardColours.lighterGrey};

  ${minBreakpointQuery.smedium`
    margin-top: 30px;
    padding: 40px 90px;
  `}

  ${minBreakpointQuery.mlarge`
    margin-top: 40px;
    padding: 50px 120px;
  `}

  ${minBreakpointQuery.large`
    margin-top: 60px;
    padding: 60px 160px;
  `}

  ${minBreakpointQuery.xlarge`
    margin-top: 70px;
    padding: 70px 190px;
  `}
`;

const StyledHeading = styled.h2`
  ${fontSize(25)}

  ${minBreakpointQuery.mlarge`
    ${fontSize(28)};
  `}

  ${minBreakpointQuery.large`
    ${fontSize(30)};
  `}
`;

const StyledForm = styled.form``;

const StyledFieldset = styled.fieldset`
  display: grid;
  row-gap: 25px;
  border: none;
  padding: 0;
  margin: 0;
  ${sectionMargins('20px', '60px')};

  ${minBreakpointQuery.medium`
    grid-template-columns: repeat(2, 1fr);
    column-gap: 20px;
  `}
`;

const StyledLegend = styled.legend`
  padding: 0;
  ${fontSize(20)};
  margin-bottom: 25px;
`;

const StyledOptionField = styled.div``;

const StyledField = styled.label`
  display: block;

  ${({ full }) => {
    if (full) {
      return css`
        ${minBreakpointQuery.medium`
          grid-column: 1 / 3;
        `}
      `;
    }
  }}
`;

const StyledLabel = styled.span`
  ${labelStyles()}

  a {
    color: ${brandColours.primary};
    text-decoration: underline;
  }
`;

const StyledInput = styled.input`
  ${inputStyles()}

  ${({ type }) => {
    if (type === 'checkbox' || type === 'radio') {
      return css`
        width: auto;
      `;
    }
  }}
`;

const StyledTextarea = styled.textarea`
  ${inputStyles()}
`;

const StyledNote = styled.span`
  display: block;
  margin-top: 5px;
  ${fontSize(13)}
`;

const StyledButtonWrapper = styled.div`
  ${minBreakpointQuery.medium`
    grid-column: 1 / 3;
  `}
`;

const StyledButton = styled(Button)`
  width: 185px;
  margin-bottom: 25px;
`;

const CreditApplicationForm = ({ heading, full }) => {
  const [submitted, setSubmitted] = useState(false);

  const fieldsets = [
    {
      heading: 'Company Details',
      items: [
        {
          name: 'Company Name',
          type: 'text',
          required: true,
        },
        {
          name: 'Registered Office Address Inc. Postcode',
          type: 'text',
          required: true,
        },
        {
          name: 'Trading Name (if different from above)',
          type: 'text',
          required: false,
        },
        {
          name: 'Contact Name',
          type: 'text',
          required: true,
        },
        {
          name: 'Company Registration Number',
          type: 'text',
          required: true,
          isFull: true,
        },
        {
          name: 'Email Address',
          type: 'email',
          required: true,
        },
        {
          name: 'VAT Registration Number',
          type: 'text',
          required: true,
          isFull: true,
        },
        {
          name: 'Telephone Number',
          type: 'tel',
          required: true,
        },
        {
          name: 'Require PO Number',
          label:
            'Do you supply / require PO numbers to be shown on our invoices in order for you to process them?',
          type: 'radio',
          options: ['Yes', 'No'],
          required: true,
          isFull: true,
        },
        {
          name: 'Previously ordered',
          label: 'Have you previously ordered from us?',
          type: 'radio',
          options: ['Yes', 'No'],
          required: true,
          isFull: true,
        },
        {
          name: 'Credit Limit Required',
          type: 'text',
          required: true,
        },
      ],
    },
    {
      heading: 'Account Details',
      items: [
        {
          name: 'Invoice / Accounts Address Inc. Postcode (if different from above)',
          type: 'text',
          required: false,
        },
        {
          name: 'Accounts Email Address',
          type: 'email',
          required: true,
        },
        {
          name: 'Accounts Contact Name',
          type: 'text',
          required: true,
        },
        {
          name: 'Invoice / Credits Email Address (if different from above)',
          type: 'email',
          required: false,
        },
        {
          name: 'Accounts Telephone Number',
          type: 'tel',
          required: true,
        },
        {
          name: 'Require PO Number',
          label:
            'Do you supply / require PO numbers to be shown on our invoices in order for you to process them?',
          type: 'radio',
          options: ['Yes', 'No'],
          required: true,
          hideOnFull: true,
        },
      ],
    },
    {
      heading: 'Bank Details',
      items: [
        {
          name: 'Name & Address of Bank Inc. Postcode',
          type: 'text',
          required: true,
        },
        {
          name: 'Account Number',
          type: 'text',
          required: true,
        },
        {
          name: 'Account Name',
          type: 'text',
          required: true,
        },
        {
          name: 'Sort Code',
          type: 'text',
          required: true,
        },
      ],
      isFull: true,
    },
    {
      heading: 'Trade References',
      items: [
        {
          name: 'Reference 1 - Company Name and Address Inc. Postcode',
          type: 'text',
          required: true,
        },
        {
          name: 'Reference 2 - Company Name and Address Inc. Postcode',
          type: 'text',
          required: true,
        },
        {
          name: 'Reference 1 - Email Address',
          type: 'email',
          required: true,
        },
        {
          name: 'Reference 2 - Email Address',
          type: 'email',
          required: true,
        },
        {
          name: 'Reference 1 - Telephone Number',
          type: 'tel',
          required: true,
        },
        {
          name: 'Reference 2 - Telephone Number',
          type: 'tel',
          required: true,
        },
      ],
      isFull: true,
    },
    {
      heading:
        'Terms and Conditions (should your credit account application be successful)',
      items: [
        {
          name: 'Accept Payment Terms',
          label:
            'Please tick to confirm that you accept out payment terms of ‘full payment to be made within 30 days from the invoice date’',
          type: 'checkbox',
          options: ['I accept the payment terms'],
          fullWidth: true,
          required: true,
        },
        {
          name: 'Accept Overdue Terms',
          label:
            'Please tick to confirm that we have made you aware that overdue invoices may be referred to a third party for collection if payment terms are not met and should this happen, we may also remove the credit facility from your account.',
          type: 'checkbox',
          options: ['I am aware of the overdue invoice terms'],
          fullWidth: true,
          required: true,
        },
        {
          name: 'Accept Website Terms',
          label:
            'Please tick to confirm that you have read our website <a href="/terms-and-conditions/" target="_blank">terms and conditions</a>.',
          type: 'checkbox',
          options: ['I have read the website terms and conditions'],
          fullWidth: true,
          required: true,
        },
      ],
    },
    {
      heading: 'Online Signature',
      items: [
        {
          name: 'Online Signature',
          type: 'text',
          note: '(This is a legal representation of your signature)',
          required: true,
        },
        {
          name: 'Position',
          type: 'text',
          required: true,
        },
        {
          name: 'Date',
          type: 'date',
          defaultValue: `${new Date().getFullYear()}-${(
            '0' +
            (new Date().getMonth() + 1)
          ).slice(-2)}-${('0' + new Date().getDate()).slice(-2)}`,
          required: true,
        },
      ],
    },
  ];

  return (
    <StyledCreditApplicationForm>
      <StyledWrapper>
        <Container>
          {!submitted ? (
            <>
              {heading && <StyledHeading>{heading}</StyledHeading>}
              <StyledForm
                name={`credit-application-form${full ? '-full' : ''}`}
                method="POST"
                data-netlify="true"
                onSubmit={async e => {
                  await submitRecaptcha(e);
                  setSubmitted(true);
                }}
              >
                <input
                  type="hidden"
                  name="form-name"
                  value={`credit-application-form${full ? '-full' : ''}`}
                />
                {fieldsets
                  .filter(fieldset => (!full ? !fieldset.isFull : fieldset))
                  .map(({ heading, items }, id) => (
                    <StyledFieldset key={id}>
                      <StyledLegend>
                        Section {id + 1} - {heading}
                      </StyledLegend>
                      {items
                        .filter(item =>
                          !full ? !item.isFull : !item.hideOnFull && item,
                        )
                        .map((item, id) => {
                          const {
                            type,
                            label,
                            name,
                            required,
                            options,
                            fullWidth,
                            defaultValue,
                            note,
                          } = item;

                          if (type === 'checkbox' || type === 'radio') {
                            return (
                              <StyledOptionField key={id} full={fullWidth}>
                                <StyledLabel
                                  dangerouslySetInnerHTML={{
                                    __html:
                                      (label ? label : name) +
                                      (required && '*'),
                                  }}
                                />
                                {options.map((option, id) => (
                                  <div key={id}>
                                    <label>
                                      <StyledInput
                                        type={type}
                                        name={slugify(name)}
                                        value={slugify(option)}
                                        required={required}
                                      />
                                      {option}
                                    </label>
                                  </div>
                                ))}
                              </StyledOptionField>
                            );
                          } else if (type === 'textarea') {
                            return (
                              <StyledField key={id} full={fullWidth}>
                                <StyledLabel>
                                  {label ? label : name}
                                  {required && '*'}
                                </StyledLabel>
                                <StyledTextarea
                                  name={slugify(name)}
                                  required={required}
                                />
                              </StyledField>
                            );
                          } else {
                            return (
                              <StyledField key={id} full={fullWidth}>
                                <StyledLabel>
                                  {label ? label : name}
                                  {required && '*'}
                                </StyledLabel>
                                <StyledInput
                                  type={type}
                                  name={slugify(name)}
                                  required={required}
                                  defaultValue={defaultValue}
                                />
                                {note && <StyledNote>{note}</StyledNote>}
                              </StyledField>
                            );
                          }
                        })}
                    </StyledFieldset>
                  ))}
                <StyledButtonWrapper>
                  <StyledButton>Submit</StyledButton>
                </StyledButtonWrapper>
                <RecaptchaText />
              </StyledForm>
            </>
          ) : (
            <p>Thanks, we'll be in touch soon</p>
          )}
        </Container>
      </StyledWrapper>
    </StyledCreditApplicationForm>
  );
};

export default CreditApplicationForm;
